import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MuslinkLogo from "@/components/MuslinkLogo";

const TEXTS = {
  en: {
    back: "Back to sign up",
    title: "Confirm your email",
    subtitle: "We sent a 6-digit code to",
    codeLabel: "Verification code",
    confirm: "Confirm",
    confirming: "Confirming...",
    noCode: "Didn't get the code?",
    resend: "Resend code",
    resendIn: (s) => `Resend code in ${s}s`,
    sent: "A new code has been sent",
    success: "Email confirmed!",
    enterCode: "Please enter the 6-digit code",
    sideTitle: "ALMOST THERE",
    sideSubtitle: "Confirm your email to keep your account secure and unlock your dashboard.",
    devHint: "Dev code (email not configured):",
  },
  ru: {
    back: "Назад к регистрации",
    title: "Подтвердите почту",
    subtitle: "Мы отправили 6-значный код на",
    codeLabel: "Код подтверждения",
    confirm: "Подтвердить",
    confirming: "Подтверждаем...",
    noCode: "Не получили код?",
    resend: "Отправить заново",
    resendIn: (s) => `Отправить заново через ${s}с`,
    sent: "Новый код отправлен",
    success: "Почта подтверждена!",
    enterCode: "Введите 6-значный код",
    sideTitle: "ПОЧТИ ГОТОВО",
    sideSubtitle: "Подтвердите почту, чтобы защитить аккаунт и открыть личный кабинет.",
    devHint: "Dev-код (почта не настроена):",
  },
  es: {
    back: "Volver al registro",
    title: "Confirma tu correo",
    subtitle: "Enviamos un código de 6 dígitos a",
    codeLabel: "Código de verificación",
    confirm: "Confirmar",
    confirming: "Confirmando...",
    noCode: "¿No recibiste el código?",
    resend: "Reenviar código",
    resendIn: (s) => `Reenviar en ${s}s`,
    sent: "Se ha enviado un nuevo código",
    success: "¡Correo confirmado!",
    enterCode: "Introduce el código de 6 dígitos",
    sideTitle: "CASI LISTO",
    sideSubtitle: "Confirma tu correo para proteger tu cuenta y acceder a tu panel.",
    devHint: "Código dev (correo no configurado):",
  },
};

const RESEND_COOLDOWN = 60;

export default function Accept() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, resendOtp } = useAuth();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const tx = TEXTS[language] || TEXTS.en;

  const email = location.state?.email;
  const [devCode, setDevCode] = useState(location.state?.devCode || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const timerRef = useRef(null);

  // No email in navigation state -> nothing to verify
  useEffect(() => {
    if (!email) {
      navigate("/register", { replace: true });
    }
  }, [email, navigate]);

  // Resend cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error(tx.enterCode);
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(email, code);
      toast.success(tx.success);
      navigate("/multilinks");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tx.enterCode);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      const data = await resendOtp(email, language);
      if (data?.dev_code) setDevCode(data.dev_code);
      toast.success(tx.sent);
      setCooldown(RESEND_COOLDOWN);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error");
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher variant="compact" />
      </div>

      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-8 py-8 sm:py-12 lg:px-16">
        <div className="w-full max-w-md mx-auto">
          <Link to="/register" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 sm:mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm sm:text-base">{tx.back}</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-6 sm:mb-8">
              <Link to="/">
                <MuslinkLogo height={32} theme={theme} />
              </Link>
            </div>

            <h1 className="text-2xl sm:text-3xl font-semibold mb-2">{tx.title}</h1>
            <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
              {tx.subtitle} <span className="text-foreground font-medium">{email}</span>
            </p>

            {devCode && (
              <div className="mb-6 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
                {tx.devHint} <span className="font-mono font-bold text-primary tracking-widest">{devCode}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm">{tx.codeLabel}</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  data-testid="accept-code-input"
                  className="h-14 sm:h-16 bg-muted border-border focus:border-primary text-center text-2xl sm:text-3xl font-semibold tracking-[0.5em]"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                data-testid="accept-submit-btn"
                className="w-full h-11 sm:h-12 bg-primary hover:bg-primary/90 rounded-xl font-semibold"
              >
                {loading ? tx.confirming : tx.confirm}
              </Button>
            </form>

            <p className="mt-6 sm:mt-8 text-center text-sm sm:text-base text-muted-foreground">
              {tx.noCode}{" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0}
                data-testid="accept-resend-btn"
                className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
              >
                {cooldown > 0 ? tx.resendIn(cooldown) : tx.resend}
              </button>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Visual (hidden on mobile/tablet) */}
      <div className={`hidden lg:flex flex-1 ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-primary/20 via-zinc-900 to-background'
          : 'bg-gradient-to-br from-primary/10 via-gray-100 to-white'
      } items-center justify-center p-12`}>
        <div className="text-center">
          <div className="w-32 h-32 xl:w-40 xl:h-40 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
            <MailCheck className="w-16 h-16 text-primary" />
          </div>
          <h2 className={`font-display text-2xl xl:text-3xl mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {tx.sideTitle}
          </h2>
          <p className={`max-w-sm ${theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'}`}>
            {tx.sideSubtitle}
          </p>
        </div>
      </div>
    </div>
  );
}
